import React, { useEffect, useMemo } from 'react'

import {
  ContainsEditableProps,
  FieldType,
  ModuleField,
  useEditor
} from '@toasttab/sites-components'

import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import { useThemeColorScheme } from 'public/components/default_template/meta/useTheme'
import { t } from 'src/translations'
import { getBorderRadiusStyle } from 'shared/utils/stylingUtils'
import { VerticalAlignment } from 'src/apollo/sites'

type TestProps = { testId?: string }

type EditableProps = {
  classNames?: string
  type: string
  color?: string
  outline?: boolean
  outlineColor?: string
  opacity?: number
  thickness?: number
  lineStyle?: string
  lineShape?: string
  verticalAlignment?: VerticalAlignment
  editableRef?: any
  overrideGlobalBorderRadius?: boolean
  borderRadius?: number
  styles?: any
} & TestProps &
  ContainsEditableProps

const DEFAULT_COLOR = '#ACACAC'
const DEFAULT_OUTLINE_COLOR = '#000000'
const DEFAULT_OPACITY = 100
const DEFAULT_THICKNESS = 2
const DEFAULT_LINE_STYLE = 'solid'
const DEFAULT_LINE_SHAPE = 'square'
const DEFAULT_VERTICAL_ALIGNMENT = VerticalAlignment.Center

const VERTICAL_ALIGNMENT_MAP: Record<VerticalAlignment, string> = {
  [VerticalAlignment.Top]: 'flex-start',
  [VerticalAlignment.Center]: 'center',
  [VerticalAlignment.Bottom]: 'flex-end'
}

export const CircleBlock = ({
  color = DEFAULT_COLOR,
  outlineColor = DEFAULT_OUTLINE_COLOR,
  outline = false,
  opacity = DEFAULT_OPACITY,
  editableRef,
  styles = {}
}: EditableProps) => {
  return (
    <div
      className='circleBlockWrapper'
      ref={editableRef}
      aria-label={t('shape-block.circle')}
    >
      <div
        className='baseShapeBlock'
        style={{
          ...styles,
          borderRadius: '100%',
          border: `${outline ? 2 : 0}px solid ${outlineColor}`,
          backgroundColor: `${color}`,
          opacity: `${opacity / 100}`
        }}
      ></div>
    </div>
  )
}

export const SquareBlock = ({
  color = DEFAULT_COLOR,
  outlineColor = DEFAULT_OUTLINE_COLOR,
  outline = false,
  opacity = DEFAULT_OPACITY,
  editableRef,
  globalBorderRadius,
  overrideGlobalBorderRadius,
  borderRadius,
  styles = {}
}: EditableProps & { globalBorderRadius?: number | null }) => {
  const radiusStyle = overrideGlobalBorderRadius
    ? getBorderRadiusStyle({ borderRadius: borderRadius ?? 0 })
    : getBorderRadiusStyle({ borderRadius: globalBorderRadius ?? undefined })

  return (
    <div
      className='baseShapeBlock'
      style={{
        border: `${outline ? 2 : 0}px solid ${outlineColor}`,
        backgroundColor: `${color}`,
        opacity: `${opacity / 100}`,
        ...radiusStyle,
        ...styles
      }}
      ref={editableRef}
      aria-label={t('shape-block.square')}
    ></div>
  )
}

export const LeafBlock = ({
  color = DEFAULT_COLOR,
  outlineColor = DEFAULT_OUTLINE_COLOR,
  outline = false,
  opacity = DEFAULT_OPACITY,
  editableRef,
  styles = {}
}: EditableProps) => {
  return (
    <div
      className='baseShapeBlock'
      style={{
        ...styles,
        border: `${outline ? 2 : 0}px solid ${outlineColor}`,
        backgroundColor: `${color}`,
        opacity: `${opacity / 100}`,
        borderRadius: '100px 100px 100px 0'
      }}
      ref={editableRef}
      aria-label={t('shape-block.leaf')}
    ></div>
  )
}

export const LineBlock = ({
  outlineColor = DEFAULT_OUTLINE_COLOR,
  opacity = DEFAULT_OPACITY,
  thickness = DEFAULT_THICKNESS,
  lineStyle = DEFAULT_LINE_STYLE,
  lineShape = DEFAULT_LINE_SHAPE,
  verticalAlignment = DEFAULT_VERTICAL_ALIGNMENT,
  editableRef,
  styles = {}
}: EditableProps) => {
  const clampedThickness = Math.min(
    Math.max(thickness || DEFAULT_THICKNESS, 1),
    20
  )
  const borderRadius =
    lineShape === 'round' && lineStyle !== 'dotted'
      ? `${clampedThickness / 2}px`
      : '0'

  return (
    <div
      className='lineBlockWrapper'
      ref={editableRef}
      aria-label={t('shape-block.line')}
      style={{
        display: 'flex',
        alignItems: VERTICAL_ALIGNMENT_MAP[verticalAlignment]
      }}
    >
      <div
        className='baseShapeBlock'
        style={{
          ...styles,
          backgroundColor:
            lineStyle === 'solid' ? `${outlineColor}` : 'transparent',
          borderTop:
            lineStyle !== 'solid'
              ? `${clampedThickness}px ${lineStyle} ${outlineColor}`
              : 'none',
          opacity: `${opacity / 100}`,
          height: lineStyle === 'solid' ? `${clampedThickness}px` : '0',
          width: '100%',
          borderRadius
        }}
      ></div>
    </div>
  )
}

export const EditableShape = (props: EditableProps) => {
  const { restaurant } = useRestaurant()
  const { borderRadius: globalBorderRadius } =
    restaurant?.config?.designConfig?.borderAndShadowConfig || {}
  const getColorFromTheme = useThemeColorScheme()
  const { meta } = restaurant
  const { editPath, color, outlineColor, outline, opacity } = props
  const { useEditableRef } = useEditor()

  const [defaultColor, defaultOutlineColor] = getColorFromTheme(
    (theme) => [
      theme.colorScheme.surface.tertiary,
      theme.colorScheme.border.default
    ],
    [meta.primaryColor, DEFAULT_OUTLINE_COLOR]
  )

  const schemaFields: ModuleField[] = useMemo(() => {
    const isLine = props.type === 'Line'
    const isSquare = props.type === 'Square'
    return [
      {
        path: `${editPath}.type`,
        displayName: 'Shape type',
        type: FieldType.Enum,
        value: props.type,
        defaultValue: 'Select shape',
        enums: [
          { displayName: 'Square', value: 'Square' },
          { displayName: 'Circle', value: 'Circle' },
          { displayName: 'Leaf', value: 'Leaf' },
          { displayName: 'Line', value: 'Line' }
        ]
      },
      {
        path: `${editPath}.opacity`,
        displayName: 'Shape opacity',
        type: FieldType.Slider,
        value: opacity || DEFAULT_OPACITY,
        defaultValue: DEFAULT_OPACITY,
        validation: { min: 0, max: 100, step: 1 }
      },
      ...(isLine
        ? [
            {
              path: `${editPath}.outlineColor`,
              displayName: 'Line color',
              type: FieldType.Color,
              value: outlineColor || defaultOutlineColor,
              defaultValue: defaultOutlineColor
            } as ModuleField,
            {
              path: `${editPath}.thickness`,
              displayName: 'Line thickness',
              type: FieldType.Slider,
              value: props.thickness || DEFAULT_THICKNESS,
              defaultValue: DEFAULT_THICKNESS,
              validation: { min: 1, max: 20, step: 1 }
            } as ModuleField,
            {
              path: `${editPath}.lineStyle`,
              displayName: 'Line style',
              type: FieldType.Enum,
              value: props.lineStyle || DEFAULT_LINE_STYLE,
              defaultValue: DEFAULT_LINE_STYLE,
              enums: [
                { displayName: 'Solid', value: 'solid' },
                { displayName: 'Dashed', value: 'dashed' },
                { displayName: 'Dotted', value: 'dotted' },
                { displayName: 'Double', value: 'double' }
              ]
            } as ModuleField,
            {
              path: `${editPath}.lineShape`,
              displayName: 'Line shape',
              type: FieldType.Enum,
              value: props.lineShape || DEFAULT_LINE_SHAPE,
              defaultValue: DEFAULT_LINE_SHAPE,
              enums: [
                { displayName: 'Square', value: 'square' },
                { displayName: 'Round', value: 'round' }
              ]
            } as ModuleField,
            {
              path: `${editPath}.verticalAlignment`,
              displayName: 'Vertical alignment',
              type: FieldType.Enum,
              value: props.verticalAlignment || DEFAULT_VERTICAL_ALIGNMENT,
              defaultValue: DEFAULT_VERTICAL_ALIGNMENT,
              enums: [
                { displayName: 'Top', value: VerticalAlignment.Top },
                { displayName: 'Center', value: VerticalAlignment.Center },
                { displayName: 'Bottom', value: VerticalAlignment.Bottom }
              ]
            } as ModuleField
          ]
        : [
            {
              path: `${editPath}.color`,
              displayName: 'Color',
              type: FieldType.Color,
              value: color || defaultColor,
              defaultValue: defaultColor
            } as ModuleField,
            {
              path: `${editPath}.outline`,
              displayName: 'Show outline',
              type: FieldType.Boolean,
              value: outline === undefined ? false : outline
            } as ModuleField,
            {
              path: `${editPath}.outlineColor`,
              displayName: 'Outline color',
              type: FieldType.Color,
              value: outlineColor || defaultOutlineColor,
              defaultValue: defaultOutlineColor
            } as ModuleField,
            ...(isSquare
              ? [
                  {
                    path: `${editPath}.overrideGlobalBorderRadius`,
                    displayName: 'Override global corner radius',
                    type: FieldType.Boolean,
                    value: props.overrideGlobalBorderRadius ?? false
                  } as ModuleField,
                  ...(props.overrideGlobalBorderRadius
                    ? [
                        {
                          path: `${editPath}.borderRadius`,
                          displayName: 'Corner radius',
                          type: FieldType.Slider,
                          value: props.borderRadius ?? 0,
                          defaultValue: 0,
                          validation: { min: 0, max: 100, step: 1 }
                        } as ModuleField
                      ]
                    : [])
                ]
              : [])
          ])
    ]
  }, [
    editPath,
    color,
    defaultColor,
    outline,
    outlineColor,
    defaultOutlineColor,
    opacity,
    props.type,
    props.thickness,
    props.lineStyle,
    props.lineShape,
    props.verticalAlignment,
    props.overrideGlobalBorderRadius,
    props.borderRadius
  ])

  const { editableRef, pushSchema } = useEditableRef<HTMLDivElement>({
    name: 'shape block',
    displayName: 'Shape',
    actions: ['delete'],
    path: editPath,
    schema: { fields: schemaFields }
  })

  useEffect(() => {
    pushSchema()
  }, [props.type, pushSchema])

  const configs = {
    ...props,
    color: props.color || defaultColor,
    outlineColor: props.outlineColor || defaultOutlineColor
  }

  switch (props.type) {
    case 'Square':
      return (
        <SquareBlock
          globalBorderRadius={globalBorderRadius}
          overrideGlobalBorderRadius={props.overrideGlobalBorderRadius}
          borderRadius={props.borderRadius}
          editableRef={editableRef}
          {...configs}
        />
      )
    case 'Circle':
      return <CircleBlock editableRef={editableRef} {...configs} />
    case 'Leaf':
      return <LeafBlock editableRef={editableRef} {...configs} />
    case 'Line':
      return <LineBlock editableRef={editableRef} {...configs} />
    default:
      return null
  }
}
